import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { authService } from '../services/authService';
import { useToastStore } from '../store/toastStore';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { FormField } from '../components/ui/FormField';
import { CheckCircleIcon } from '../components/ui/Icons';

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const showToast = useToastStore((s) => s.show);
  const [searchParams] = useSearchParams();

  const token = searchParams.get('token');
  const email = searchParams.get('email');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const passwordsMatch = newPassword === confirmPassword;
  const canSubmit = newPassword.length > 0 && confirmPassword.length > 0 && passwordsMatch;

  const handleSubmit = async (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!passwordsMatch) {
      showToast('As senhas não coincidem.', 'error');
      return;
    }

    if (!token || !email) return;

    setLoading(true);
    try {
      await authService.resetPassword({ email, token, newPassword });
      setSuccess(true);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { errors?: string[] } } };
      showToast(axiosErr?.response?.data?.errors?.[0] ?? 'Erro ao redefinir a senha. Tente novamente.', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!token || !email) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-sm p-8 w-full max-w-sm text-center">
          <h1 className="text-2xl font-bold text-text-dark mb-2">Link inválido</h1>
          <p className="text-text-light text-sm mb-4">
            O link de redefinição de senha é inválido ou expirou.
          </p>
          <Link to="/esqueci-senha" className="text-chocolate hover:underline text-sm">
            Solicitar novo link
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-sm p-8 w-full max-w-sm text-center">
          <div className="flex justify-center mb-3">
            <CheckCircleIcon className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-text-dark mb-2">Senha redefinida!</h1>
          <p className="text-text-light text-sm mb-6">
            Sua senha foi alterada com sucesso. Agora você já pode fazer login com a nova senha.
          </p>
          <Button fullWidth onClick={() => navigate('/login')}>
            Ir para o login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-sm p-8 w-full max-w-sm">
        <h1 className="text-2xl font-bold text-text-dark mb-1">Redefinir senha</h1>
        <p className="text-text-light text-sm mb-6">{email}</p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <FormField label="Nova senha" required>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="new-password"
              required
            />
          </FormField>

          <FormField label="Confirmar senha" required>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="new-password"
              required
            />
          </FormField>

          {confirmPassword.length > 0 && !passwordsMatch && (
            <p className="text-danger text-xs -mt-2">As senhas não coincidem.</p>
          )}

          <Button type="submit" fullWidth loading={loading} disabled={!canSubmit}>
            Redefinir senha
          </Button>
        </form>

        <p className="text-center text-sm text-text-light mt-4">
          <Link to="/login" className="text-chocolate hover:underline">
            Voltar para o login
          </Link>
        </p>
      </div>
    </div>
  );
}
